import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ReddyCasinoPage from './pages/ReddyCasinoPage';
import MiniLobby from './components/MiniLobby';
import WalletPage from './pages/WalletPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import NotificationsPage from './pages/NotificationsPage';
import ReddyProfilePage from './pages/ReddyProfilePage';
import ReddyAccountStatementPage from './pages/ReddyAccountStatementPage';
import UnsettledBetsPage from './pages/UnsettledBetsPage';
import DepositPage from './pages/DepositPage';
import WalletDepositPage from './pages/WalletDepositPage';
import WithdrawPage from './pages/WithdrawPage';
import AddAccountPage from './pages/AddAccountPage';
import WinnerCupPage from './pages/WinnerCupPage';
import FifaCupWinnerPage from './pages/FifaCupWinnerPage';
import SettingsPage from './pages/SettingsPage';
import GameWebView from './components/GameWebView';
import AllGamesPage from './pages/AllGamesPage';
import CategoryPage from './pages/CategoryPage';
import RulesPage from './pages/RulesPage';
import ReferralPage from './pages/ReferralPage';
import ForceChangePasswordPage from './pages/ForceChangePasswordPage';
import { Toaster } from './components/ui/toaster';
import Footer from './components/Footer';
import MobileBottomNav from './components/MobileBottomNav';
import ReddyHomePage from './pages/HomePage';
import MatchPage from './pages/MatchPage';
import ReddySportPage from './pages/ReddySportPage';
import InPlayPage from './pages/InPlayPage';
import ReddyLayout from './components/home/ReddyLayout';

/* withReddy — wrap an old page in the reddy chrome (ReddyLayout provides header + marquee + SportsNav + sidebar + footer + mobile bottom-nav). Used to bring account/utility/game pages onto the reddy design without rebuilding them. */
const withReddy = (Component, props = {}) => (
  <ReddyLayout>
    <Component {...props} />
  </ReddyLayout>
);

/* HomePage — now the pixel-perfect reddy homepage in pages/HomePage.jsx (imported as ReddyHomePage) */

/* ═══════════════════════════════════════════
   App
   ═══════════════════════════════════════════ */
function App() {
  const [user, setUser] = useState(null);
  const [activeSport, setActiveSport] = useState('Cricket');
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isAuthRoute = location.pathname === '/login' || location.pathname === '/register' || location.pathname.startsWith('/register/') || location.pathname === '/forgot-password' || location.pathname === '/force-change-password';
  const isForcePasswordRoute = location.pathname === '/force-change-password';
  const mustChangePassword = user && localStorage.getItem('must_change_password') === 'true';
  const [hasLoadedUser, setHasLoadedUser] = useState(false);

  // Pages that get the 3-column sport layout
  const sportPaths = ['/', '/cricket', '/soccer', '/tennis', '/volleyball', '/mma', '/kabaddi',
    '/basketball', '/baseball', '/table-tennis', '/ice-hockey', '/rugby', '/darts', '/futsal',
    '/sportsbook', '/esports', '/election', '/horse-racing', '/greyhound-racing', '/binary',
    '/handball', '/australian-rules', '/american-football', '/snooker', '/gaelic',
    '/indian-card-games', '/winner-cup', '/fifa-cup-winner', '/referral'];
  const isHome = location.pathname === '/';
  const isMatch = location.pathname.startsWith('/match/');
  const isCasino = location.pathname === '/casino';
  const isInplay = location.pathname === '/inplay';
  const isGame = location.pathname === '/game';
  const isProfile = location.pathname === '/profile' || location.pathname.startsWith('/profile/');
  const isStatement = location.pathname === '/reports/account-statement';
  const isUnsettled = location.pathname === '/reports/unsettled-bets';
  const isSport = location.pathname.startsWith('/sport/') || ['/cricket', '/soccer', '/tennis', '/horse-racing', '/kabaddi', '/election', '/sportsbook', '/greyhound-racing', '/binary', '/basketball', '/baseball', '/table-tennis', '/volleyball', '/ice-hockey', '/rugby', '/mma', '/darts', '/futsal', '/esports', '/handball', '/australian-rules', '/american-football', '/snooker', '/gaelic'].includes(location.pathname);
  // account/wallet/utility pages wrapped in reddy chrome via withReddy — must be in isReddyPage so the old global header is suppressed
  const isAccount = ['/deposit', '/withdraw', '/wallet', '/wallet-deposit', '/add-account', '/referral', '/notifications', '/settings', '/all-games', '/rules', '/winner-cup', '/fifa-cup-winner'].includes(location.pathname) || location.pathname.startsWith('/category/');
  const isReddyPage = isHome || isMatch || isCasino || isInplay || isProfile || isStatement || isUnsettled || isSport || isAccount; // self-contained pages (own header/nav/sidebar via ReddyLayout)
  const isSportPage = !isReddyPage && sportPaths.includes(location.pathname);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('reddyannabook_user');
      if (stored) setUser(JSON.parse(stored));
    } catch (_) {}
    setHasLoadedUser(true);
    // Register Service Worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  // Force-redirect to /force-change-password if must_change_password is set
  useEffect(() => {
    if (mustChangePassword && !isForcePasswordRoute && !isAuthRoute) {
      navigate('/force-change-password', { replace: true });
    }
  }, [mustChangePassword, isForcePasswordRoute, isAuthRoute, location.pathname]);

  useEffect(() => {
    if (!hasLoadedUser) return;
    try {
      if (user) localStorage.setItem('reddyannabook_user', JSON.stringify(user));
      else localStorage.removeItem('reddyannabook_user');
    } catch (_) {}
  }, [user, hasLoadedUser]);

  // JWT auto-refresh — every 30 minutes
  useEffect(() => {
    const refreshToken = async () => {
      const token = localStorage.getItem('reddyannabook_token');
      if (!token) return;
      try {
        const res = await fetch('/api/auth/refresh-token', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        let data = null;
        try { data = await res.json(); } catch {}
        if (data?.success && data?.token) {
          localStorage.setItem('reddyannabook_token', data.token);
        } else if (res.status === 401) {
          localStorage.clear();
          window.location.href = '/login?reason=expired';
        }
      } catch (e) {
        console.error('Token refresh failed', e);
      }
    };
    const interval = setInterval(refreshToken, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Idle auto-logout — 3 hours of inactivity
  useEffect(() => {
    let idleTimer;
    const IDLE_TIMEOUT = 3 * 60 * 60 * 1000; // 3 hours
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        localStorage.clear();
        window.location.href = '/login?reason=idle';
      }, IDLE_TIMEOUT);
    };
    events.forEach((ev) => document.addEventListener(ev, resetIdleTimer, { capture: true, passive: true }));
    resetIdleTimer();
    return () => {
      clearTimeout(idleTimer);
      events.forEach((ev) => document.removeEventListener(ev, resetIdleTimer, { capture: true }));
    };
  }, []);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-kabook-bg">
      {!isAuthRoute && !isReddyPage && !isGame && (
        <>
          <Header user={user} setUser={setUser} onMenuClick={() => setMobileMenuOpen(true)} onNavigate={() => setMobileMenuOpen(false)} />
          {mobileMenuOpen && (
            <div className="fixed inset-0 z-50 md:hidden">
              <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
              <div className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-lg animate-slide-in">
                <Sidebar activeSport={activeSport} setActiveSport={setActiveSport} onNavigate={() => setMobileMenuOpen(false)} />
              </div>
            </div>
          )}
          {/* NotificationBar removed — replaced by public /api/ticker in Header */}
          {/* WalletQuickActions removed from homepage — available in sidebar/header */}
        </>
      )}

      <div className={`flex-1 min-h-0 ${isSportPage ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'}`} role="main">
        <Routes>
          {/* HOME — pixel-perfect reddy homepage (self-contained header/nav/sidebar/footer) */}
          <Route path="/" element={<ReddyHomePage />} />

          {/* IN-PLAY — live matches across all sports; same homepage sport-table structure */}
          <Route path="/inplay" element={<InPlayPage />} />

          {/* SPORT PAGES — reddy-styled via /sport/:sport; the old sport URLs redirect in
              (so existing sidebar/nav links keep working). Others stay old-layout for now. */}
          <Route path="/sport/:sport" element={<ReddySportPage />} />
          <Route path="/cricket" element={<Navigate to="/sport/cricket" replace />} />
          <Route path="/soccer" element={<Navigate to="/sport/soccer" replace />} />
          <Route path="/tennis" element={<Navigate to="/sport/tennis" replace />} />
          <Route path="/volleyball" element={<Navigate to="/sport/volleyball" replace />} />
          <Route path="/mma" element={<Navigate to="/sport/mma" replace />} />
          <Route path="/kabaddi" element={<Navigate to="/sport/kabaddi" replace />} />
          <Route path="/basketball" element={<Navigate to="/sport/basketball" replace />} />
          <Route path="/baseball" element={<Navigate to="/sport/baseball" replace />} />
          <Route path="/table-tennis" element={<Navigate to="/sport/table-tennis" replace />} />
          <Route path="/ice-hockey" element={<Navigate to="/sport/ice-hockey" replace />} />
          <Route path="/rugby" element={<Navigate to="/sport/rugby" replace />} />
          <Route path="/darts" element={<Navigate to="/sport/darts" replace />} />
          <Route path="/futsal" element={<Navigate to="/sport/futsal" replace />} />
          <Route path="/sportsbook" element={<Navigate to="/sport/sportsbook" replace />} />
          <Route path="/esports" element={<Navigate to="/sport/esports" replace />} />
          <Route path="/election" element={<Navigate to="/sport/election" replace />} />
          <Route path="/horse-racing" element={<Navigate to="/sport/horse-racing" replace />} />
          <Route path="/greyhound-racing" element={<Navigate to="/sport/greyhound-racing" replace />} />
          <Route path="/binary" element={<Navigate to="/sport/binary" replace />} />
          <Route path="/handball" element={<Navigate to="/sport/handball" replace />} />
          <Route path="/australian-rules" element={<Navigate to="/sport/australian-rules" replace />} />
          <Route path="/american-football" element={<Navigate to="/sport/american-football" replace />} />
          <Route path="/snooker" element={<Navigate to="/sport/snooker" replace />} />
          <Route path="/gaelic" element={<Navigate to="/sport/gaelic" replace />} />

          {/* GAMES — with sidebar */}
          <Route path="/indian-card-games" element={<Navigate to="/casino" replace />} />
          <Route path="/winner-cup" element={withReddy(WinnerCupPage)} />
          <Route path="/fifa-cup-winner" element={withReddy(FifaCupWinnerPage)} />

          {/* MATCH PAGE — pixel-perfect reddy match page (self-contained via ReddyLayout) */}
          <Route path="/match/:matchId" element={<MatchPage />} />

          {/* CASINO — pixel-perfect reddy casino page (self-contained via ReddyLayout) */}
          <Route path="/casino" element={<ReddyCasinoPage />} />

          {/* UTILITY PAGES — old pages wrapped in reddy chrome via withReddy */}
          <Route path="/wallet" element={withReddy(WalletPage)} />
          {/* MY PROFILE — pixel-perfect reddy profile (3 tabs, self-contained via ReddyLayout) */}
          <Route path="/profile" element={<ReddyProfilePage />} />
          <Route path="/profile/stake-settings" element={<ReddyProfilePage />} />
          <Route path="/profile/change-password" element={<ReddyProfilePage />} />
          {/* ACCOUNT STATEMENT — pixel-perfect reddy statement */}
          <Route path="/reports/account-statement" element={<ReddyAccountStatementPage />} />
          {/* UNSETTLED BETS — post-login table page */}
          <Route path="/reports/unsettled-bets" element={<UnsettledBetsPage />} />
          <Route path="/force-change-password" element={<ForceChangePasswordPage setUser={setUser} />} />
          <Route path="/login" element={<LoginPage setUser={setUser} />} />
          <Route path="/register" element={<RegisterPage setUser={setUser} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/register/:referralCode" element={<RegisterPage />} />
          <Route path="/notifications" element={withReddy(NotificationsPage)} />
          <Route path="/deposit" element={withReddy(DepositPage)} />
          <Route path="/wallet-deposit" element={withReddy(WalletDepositPage)} />
          <Route path="/withdraw" element={withReddy(WithdrawPage)} />
          <Route path="/add-account" element={withReddy(AddAccountPage)} />
          <Route path="/settings" element={withReddy(SettingsPage)} />
          {/* GAME — fullscreen casino iframe; no chrome (not in isReddyPage, isGame suppresses old header) */}
          <Route path="/game" element={<GameWebView />} />
          <Route path="/all-games" element={withReddy(AllGamesPage)} />
          <Route path="/category/:category" element={withReddy(CategoryPage)} />
          <Route path="/rules" element={withReddy(RulesPage)} />
          <Route path="/referral" element={withReddy(ReferralPage)} />
        </Routes>
        {!isAuthRoute && !isSportPage && !isReddyPage && <Footer />}
      </div>

      {/* Mobile bottom navigation */}
      {/* old-app mobile bottom nav — NOT on reddy pages (they have the reddy .footer_mobilemenu) */}
      {!isAuthRoute && !isReddyPage && !isGame && <MobileBottomNav user={user} />}

      <Toaster />

      {/* Mini Casino Lobby - mobile only, hidden on casino/cricket pages */}
      {/* old-app mini casino lobby — NOT on reddy pages (stray "Casino" element bled through on mobile) */}
      {!isAuthRoute && !isReddyPage && !isGame && !['/casino', '/cricket'].includes(location.pathname) && <MiniLobby />}
    </div>
  );
}

export default App;
